import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { useHeartbeats } from '@/hooks/useWebSocket';
import { useRestaurantId } from '@/hooks/useRestaurantId';
import type { PosHeartbeatMessage, TerminalStatus } from '@/types/api';
import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, Plus, RefreshCw, Settings, Info } from 'lucide-react';
import { RestaurantTitleSuffix } from '@/components/restaurant/RestaurantTitleSuffix';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';
import type { TerminalWithHeartbeat } from '@/components/terminals/types';
import RegisterTerminalModal from '@/components/terminals/RegisterTerminalModal';
import TerminalDetailsModal from '@/components/terminals/TerminalDetailsModal';
import TerminalSettingsModal from '@/components/terminals/TerminalSettingsModal';

// POS terminals heartbeat every 60s (see backend TerminalHealthScheduler); anything
// older than 1.5x that interval is treated as offline even if the last known status
// was ONLINE, instead of waiting on the backend's slower (2min poll / 5min threshold)
// sweep to flip it and a refetch to pick that up.
const STALE_HEARTBEAT_MS = 90_000;

export default function TerminalsPage() {
  const restaurantId = useRestaurantId();
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTerminalId, setSelectedTerminalId] = useState<number | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const canCreate = useHasPermission(PERM.TERMINAL_CREATE);
  const canUpdate = useHasPermission(PERM.TERMINAL_UPDATE);

  // Fetch terminals
  const { data: fetchedTerminals = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['terminals', restaurantId],
    queryFn: () => apiClient.getTerminals(restaurantId!),
    enabled: restaurantId !== null,
  });

  // Live heartbeat overlay, keyed by terminalId. Kept separate from the query
  // cache so a background refetch of `terminals` never discards heartbeats
  // received since the last fetch (they aren't persisted anywhere else).
  const [heartbeats, setHeartbeats] = useState<Map<string, PosHeartbeatMessage>>(new Map());

  const handleHeartbeat = useCallback((message: PosHeartbeatMessage) => {
    setHeartbeats((prev) => {
      const next = new Map(prev);
      next.set(message.terminalId, message);
      return next;
    });
  }, []);

  useHeartbeats(restaurantId !== null ? String(restaurantId) : '', handleHeartbeat);

  // Re-render every minute so lastHeartbeatDistance and the staleness-based
  // isOnline check below stay current even with no new heartbeat or refetch.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  // Derived display data: fetchedTerminals (source of truth) with the live
  // heartbeat overlay patched on top. Recomputed from scratch every time
  // either input changes, so an empty refetch or a deleted terminal is
  // reflected immediately instead of being masked by a stale local copy.
  const terminals = useMemo<TerminalWithHeartbeat[]>(() => {
    void tick; // re-run this memo on each 60s tick, see effect above
    const now = Date.now();
    return fetchedTerminals.map((t) => {
      const live = heartbeats.get(t.terminalId);
      const status = (live?.status ?? t.status) as TerminalStatus;
      const lastHeartbeat = live?.timestamp ?? t.lastHeartbeat;
      const lastHeartbeatMs = lastHeartbeat ? new Date(lastHeartbeat).getTime() : null;
      const isOnline =
        status === 'ONLINE' && lastHeartbeatMs !== null && now - lastHeartbeatMs < STALE_HEARTBEAT_MS;

      return {
        ...t,
        status,
        lastHeartbeat,
        lastHeartbeatDistance: lastHeartbeat
          ? formatDistanceToNow(new Date(lastHeartbeat), { addSuffix: true })
          : undefined,
        isOnline,
      };
    });
  }, [fetchedTerminals, heartbeats, tick]);

  const openDetailsModal = (terminal: TerminalWithHeartbeat) => {
    setSelectedTerminalId(terminal.id);
    setIsDetailsOpen(true);
  };

  const openSettingsModal = (terminal: TerminalWithHeartbeat) => {
    setSelectedTerminalId(terminal.id);
    setIsSettingsOpen(true);
  };

  // Re-derived from the live `terminals` array (not a snapshot captured at click
  // time) so the modals always reflect the latest heartbeat/refetch data, and a
  // Settings save PUTs against current data rather than a stale frozen copy.
  const selectedTerminal = terminals.find((t) => t.id === selectedTerminalId) ?? null;

  const stats = {
    total: terminals.length,
    online: terminals.filter((t) => t.isOnline).length,
    offline: terminals.filter((t) => !t.isOnline).length,
  };

  if (restaurantId === null) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <AlertTriangle className="h-8 w-8 text-red-500" />
        <div className="text-gray-900 font-medium">Invalid restaurant ID</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <AlertTriangle className="h-8 w-8 text-red-500" />
        <div className="text-gray-900 font-medium">Failed to load terminals</div>
        <button onClick={() => refetch()} className="btn btn-outline h-10">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading terminals...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            POS Terminal Monitoring
            <RestaurantTitleSuffix restaurantId={restaurantId} />
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Real-time terminal status and heartbeat monitoring
          </p>
        </div>

        <div className="flex items-center space-x-4">
          {canCreate && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="btn btn-primary h-10"
            >
              <Plus className="h-4 w-4 mr-2" />
              Register Terminal
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats.total > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <div className="card p-6">
            <div className="text-sm text-gray-600">Total Terminals</div>
            <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="card p-6">
            <div className="text-sm text-gray-600">Online</div>
            <div className="text-3xl font-bold text-green-600">
              {stats.online}
            </div>
          </div>
          <div className="card p-6">
            <div className="text-sm text-gray-600">Offline</div>
            <div className="text-3xl font-bold text-red-600">{stats.offline}</div>
          </div>
        </div>
      )}

      {/* Terminals Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {terminals.map((terminal) => (
          <div
            key={terminal.id}
            className={`card p-6 ${
              terminal.isOnline ? 'border-green-300' : 'border-red-300'
            } border-2`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {terminal.name}
                </h3>
                <p className="text-sm text-gray-500 font-mono">
                  {terminal.terminalId}
                </p>
              </div>
              <div className="flex flex-col items-end space-y-2">
                <span
                  className={`badge ${
                    terminal.isOnline ? 'badge-success' : 'badge-danger'
                  }`}
                >
                  {terminal.status}
                </span>
                {terminal.isOnline && (
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-xs text-green-600">Live</span>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Location</span>
                <span className="font-medium">{terminal.location}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600">IP Address</span>
                <span className="font-mono text-xs">{terminal.ipAddress}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600">Version</span>
                <span className="font-medium">{terminal.appVersion || 'N/A'}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600">Hardware</span>
                <span className="font-medium">{terminal.deviceModel || 'N/A'}</span>
              </div>

              {terminal.lastHeartbeat && (
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Last Heartbeat</span>
                    <span
                      className={`font-medium ${
                        terminal.isOnline ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {terminal.lastHeartbeatDistance}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 flex space-x-2">
              <button
                onClick={() => openDetailsModal(terminal)}
                className="btn btn-outline flex-1 h-9 text-sm"
              >
                <Info className="h-4 w-4 mr-1" />
                Details
              </button>
              {canUpdate && (
                <button
                  onClick={() => openSettingsModal(terminal)}
                  className="btn btn-ghost flex-1 h-9 text-sm"
                >
                  <Settings className="h-4 w-4 mr-1" />
                  Settings
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {terminals.length === 0 && (
        <div className="card p-12 text-center">
          <p className="text-gray-500">No terminals registered</p>
          {canCreate && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="btn btn-primary mt-4 h-10"
            >
              <Plus className="h-4 w-4 mr-2" />
              Register First Terminal
            </button>
          )}
        </div>
      )}

      {/* Register Terminal Modal */}
      {isModalOpen && (
        <RegisterTerminalModal
          restaurantId={restaurantId}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['terminals', restaurantId] });
            setIsModalOpen(false);
          }}
        />
      )}

      {/* Terminal Details Modal */}
      {isDetailsOpen && selectedTerminal && (
        <TerminalDetailsModal
          terminal={selectedTerminal}
          onClose={() => {
            setIsDetailsOpen(false);
            setSelectedTerminalId(null);
          }}
        />
      )}

      {/* Terminal Settings Modal */}
      {isSettingsOpen && selectedTerminal && (
        <TerminalSettingsModal
          terminal={selectedTerminal}
          onClose={() => {
            setIsSettingsOpen(false);
            setSelectedTerminalId(null);
          }}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['terminals', restaurantId] });
            setIsSettingsOpen(false);
            setSelectedTerminalId(null);
          }}
          onDeleted={() => {
            queryClient.invalidateQueries({ queryKey: ['terminals', restaurantId] });
            setIsSettingsOpen(false);
            setSelectedTerminalId(null);
          }}
        />
      )}
    </div>
  );
}
